import { Signal } from 'typed-signals';
import { getLocalizedText } from '@softgames/localization';
import type { Popup } from '../../popups/Popup';
import type { BlockerIntroConfig } from './constants';
import { BlockerIntroductionPopupLayout } from '../../../layouts/blockers/BlockerIntroductionPopupLayout';
import { applyLayoutState, revokeLayoutState } from '../../libs/layouts/populateLayouts/applyLayoutState';
import type { TextWithColorTags } from '../../libs/layouts/layoutPostProcessors/layoutColorTagsSPL';
import { processColorTags } from '../../libs/layouts/layoutPostProcessors/layoutColorTagsSPL';
import { BoardTutorialController } from '../tutorials/board/BoardTutorialController';
import type { BoardTutorial } from '../tutorials/board/types';
import { playLayoutAnimation } from '../../libs/layouts/layoutAnimations/playLayoutAnimation';
import { triggerParticleFX } from '../../libs/particles/triggerParticleFX';

export class BlockerIntroductionPopup implements Popup {
  public readonly onButtonClicked = new Signal();

  private readonly layout = new BlockerIntroductionPopupLayout();

  public readonly root = this.layout.root;

  private playbackController: BoardTutorialController | null = null;

  constructor(config: BlockerIntroConfig) {
    void this.init(config);
  }

  private async init(config: BlockerIntroConfig): Promise<void> {
    const { description, header, icon, particleSlot } = this.layout;

    header.text = getLocalizedText(config.title);
    description.text = getLocalizedText(config.description);

    processColorTags(header as unknown as TextWithColorTags);
    processColorTags(description as unknown as TextWithColorTags);

    icon.props({
      texture: config.icon,
    });

    if (config.tutorialData) applyLayoutState(this.layout, 'tutorial');

    void triggerParticleFX('Blocker_Intro_Particles', particleSlot);

    this.layout.actionButton.on('pointertap', () => {
      this.onButtonClicked.emit();
    });
  }

  public update(deltaTime: number): void {
    if (this.playbackController) this.playbackController.update(deltaTime);
  }

  public async showTutorial(tutorial: BoardTutorial): Promise<void> {
    const { actionButton, tutorialSlot } = this.layout;

    actionButton.eventMode = 'none';
    await playLayoutAnimation(this.layout, 'ShowTutorial', false, (event) => {
      if (event === 'showTutorial') {
        revokeLayoutState(this.layout, 'tutorial');

        this.playbackController = new BoardTutorialController({
          parent: tutorialSlot,
          maxWidth: 650,
          maxHeight: 460,
        }).playTutorial(tutorial, true);
      }
    });
    actionButton.eventMode = 'static';
  }

  public onAdded(): void {
    this.root.tweenFrom({ alpha: 0 }, 300, 'sinIn', true);
  }
}
